'use client';

import 'BomdisoftComponents/draggable/indicators/styles.css';
import { type DragDirection, delegateVerticalDragIndicator } from 'BomdisoftComponents/draggable/indicators/delegate';
import { joinClasses } from 'BomdisoftComponents/html/index';
import { DragButton } from 'BomdisoftComponents/icons/index';
import { Tooltip } from 'BomdisoftComponents/tooltip/index';
import { getLanguageFromLocale, resizeText } from '@bomdisoft/translations';
import { useParams } from 'next/navigation';
import { useEffect, useRef } from 'react';

type VerticalDragIndicatorProps = {
  callback: (position: number) => void;
  dragDirection: DragDirection;
  minResizeWidth: number;
  maxResizeWidth: number;
  className?: string;
};

export const VerticalDragIndicator = ({ callback, dragDirection, minResizeWidth, maxResizeWidth, className }: VerticalDragIndicatorProps) => {
  const params = useParams();
  const label = resizeText[getLanguageFromLocale(params?.lang as string)];
  const containerRef = useRef<HTMLDivElement | null>(null);
  const callbackRef = useRef(callback);

  callbackRef.current = callback;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    return delegateVerticalDragIndicator(container, { dragDirection, onResize: width => callbackRef.current(width) });
  }, [dragDirection]);

  return (
    <div className={joinClasses('bomdisoft-drag-indicator', className)} data-testid='vertical-drag-indicator' ref={containerRef}>
      <div aria-hidden='true' className='bomdisoft-drag-indicator-line' data-testid='vertical-drag-indicator-line' />
      <div className='bomdisoft-drag-indicator-track'>
        <Tooltip placement='left' title={label}>
          <hr aria-label={label} aria-orientation='vertical' aria-valuemax={maxResizeWidth} aria-valuemin={minResizeWidth} className='bomdisoft-drag-indicator-handle' data-testid='vertical-drag-indicator-handle' tabIndex={0} />
          <DragButton />
        </Tooltip>
      </div>
    </div>
  );
};
